<template>
  <app-page :title="$t('@revenueDashboardPage/title')">
    <v-layout>
      <v-flex xs12>
        <v-menu
          ref="menu"
          :close-on-content-click="false"
          v-model="menu"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        ></v-menu>
        <v-card class="pa-4">
          <v-layout>
            <v-flex xs6 class="pr-2">
              <v-dialog
                ref="dialog"
                v-model="modal"
                :return-value.sync="date"
                persistent
                lazy
                width="290px"
              >
                <v-text-field
                  slot="activator"
                  v-model="date"
                  :label="$t('@reportsPage/monthFilterDatepickerLabel')"
                  readonly
                ></v-text-field>
                <v-date-picker v-model="date" type="month" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="modal = false">
                    {{ $t('@reportsPage/monthFilterDatepickerCancelButtonLabel') }}
                  </v-btn>
                  <v-btn flat color="primary" @click="$refs.dialog.save(date)">
                    {{ $t('@reportsPage/monthFilterDatepickerSubmitButtonLabel') }}
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </v-flex>
            <v-flex xs6 class="pl-2">
              <v-select
                :items="productGroups"
                v-model="selectedProductGroup"
                :label="$t('@revenueDashboardPage/productGroupSelectLabel')"
                item-value="value"
                item-text="key"
                return-object
                :disabled="!canSelectProduct"
              ></v-select>
            </v-flex>
          </v-layout>
          <v-data-table :headers="headers" :items="items" :loading="loading" hide-actions>
            <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
            <template slot="items" slot-scope="props">
              <tr>
                <grid-cell
                  :header="header"
                  :row="props.item"
                  :key="index"
                  v-for="(header, index) in headers"
                ></grid-cell>
              </tr>
            </template>
          </v-data-table>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn flat color="pink" @click="getBillingData" :disabled="!requestEnabled">
              {{ $t('@revenueDashboardPage/submitButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </app-page>
</template>

<script>
import GridCell from '../../components/GridCell';
import moment from 'moment';
import 'moment/locale/sv';
import { serializeQuery, mergeById } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';

moment.locale('sv');

const REQUEST_QUERY_DATE_FORMAT = 'YYYY-MM-DD';

const productsMap = {
  TF: 1,
  OF: 2,
  SF: 3,
  all: 4,
};

const monthsMap = {
  Jan: 'jan',
  Feb: 'feb',
  Mar: 'mar',
  Apr: 'apr',
  May: 'may',
  Jun: 'jun',
  Jul: 'jul',
  Aug: 'aug',
  Sep: 'sep',
  Oct: 'oct',
  Nov: 'nov',
  Dec: 'dec',
};

export default {
  components: {
    GridCell,
  },

  data() {
    return {
      modal: null,
      menu: null,
      date: null,
      loading: false,
      pagination: false,
      items: [],
      errorMessage: '',
      selectedProductGroup: null,
      productGroups: [
        { key: this.$t('@insurancesList/allInsurancesGroupName'), value: productsMap.all },
        { key: this.$t('@reportsPage/productGroupSelectTfOption'), value: productsMap.TF },
        { key: this.$t('@reportsPage/productGroupSelectOfOption'), value: productsMap.OF },
        { key: this.$t('@reportsPage/productGroupSelectSfOption'), value: productsMap.SF },
      ],
      headers: [
        {
          text: this.$t('@debtsList/periodCostColumnHeader'),
          align: 'left',
          sortable: false,
          value: 'name',
        },
        {
          text: moment()
            .month(0)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Jan,
        },
        {
          text: moment()
            .month(1)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Feb,
        },
        {
          text: moment()
            .month(2)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Mar,
        },
        {
          text: moment()
            .month(3)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Apr,
        },
        {
          text: moment()
            .month(4)
            .format('MMM'),
          sortable: false,
          value: monthsMap.May,
        },
        {
          text: moment()
            .month(5)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Jun,
        },
        {
          text: moment()
            .month(6)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Jul,
        },
        {
          text: moment()
            .month(7)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Aug,
        },
        {
          text: moment()
            .month(8)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Sep,
        },
        {
          text: moment()
            .month(9)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Oct,
        },
        {
          text: moment()
            .month(10)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Nov,
        },
        {
          text: moment()
            .month(11)
            .format('MMM'),
          sortable: false,
          value: monthsMap.Dec,
        },
      ],
    };
  },

  computed: {
    canSelectProduct() {
      return (this.selectedProductGroup && this.selectedProductGroup.value) !== 'advisor';
    },
    requestEnabled() {
      return this.date && (this.canSelectProduct ? this.selectedProductGroup : true);
    },
  },

  watch: {},

  methods: {
    save(date) {
      this.$refs.menu.save(date);
      this.menu = false;
    },
    async getBillingData() {
      if (!this.requestEnabled) {
        return;
      }

      try {
        this.loading = true;
        const date = moment(this.date)
          .startOf('month')
          .format(REQUEST_QUERY_DATE_FORMAT);

        const query = { date };

        if (this.selectedProductGroup) {
          query.productGroup = this.selectedProductGroup.value;
        }

        let qs = serializeQuery(query);

        const currentMonthBillingData = await this.$http.get(`/api/stat/billingDashboard?${qs}`);

        query.date = moment(this.date)
          .startOf('month')
          .subtract(1, 'months')
          .format(REQUEST_QUERY_DATE_FORMAT);

        qs = serializeQuery(query);

        const lastMonthBillingData = await this.$http.get(`/api/stat/billingDashboard?${qs}`);

        query.date = moment(this.date)
          .startOf('month')
          .add(1, 'months')
          .format(REQUEST_QUERY_DATE_FORMAT);

        qs = serializeQuery(query);

        const nextMonthBillingData = await this.$http.get(`/api/stat/billingDashboard?${qs}`);

        const currentMonthBillingDataValues = Object.values(currentMonthBillingData.data);
        const lastMonthBillingDataValues = Object.values(lastMonthBillingData.data);
        const nextMonthBillingDataValues = Object.values(nextMonthBillingData.data);
        let itemsData = mergeById(lastMonthBillingDataValues, currentMonthBillingDataValues);

        itemsData = mergeById(itemsData, nextMonthBillingDataValues);

        this.items = Object.values(itemsData);

        this.errorMessage = '';
        this.loading = false;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
